import FileDropzone from '@v2/components/ui/FileDropzone';
import { cn } from '@v2/utils/cn';
import toast from 'react-hot-toast';

export const settingsInputClass =
    'w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 shadow-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:focus:border-gray-500 dark:focus:ring-gray-100/10';

type FormRowProps = {
    label: string;
    hint?: string;
    required?: boolean;
    error?: string;
    children: React.ReactNode;
};

export function SettingsFormRow({ label, hint, required, error, children }: FormRowProps) {
    return (
        <div className="grid grid-cols-1 gap-4 border-b border-gray-100 py-6 last:border-b-0 dark:border-gray-700/50 md:grid-cols-12 md:gap-8">
            <div className="md:col-span-4">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {label}
                    {required && <span className="text-red-500"> *</span>}
                </p>
                {hint && <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{hint}</p>}
            </div>
            <div className="md:col-span-8">
                {children}
                {error && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p>}
            </div>
        </div>
    );
}

type PrefixInputProps = {
    prefix: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    type?: string;
    disabled?: boolean;
};

export function PrefixInput({ prefix, value, onChange, placeholder, type = 'text', disabled }: PrefixInputProps) {
    return (
        <div className="flex overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm focus-within:border-gray-400 focus-within:ring-2 focus-within:ring-gray-900/10 dark:border-gray-600 dark:bg-gray-800 dark:focus-within:border-gray-500 dark:focus-within:ring-gray-100/10">
            <span className="flex shrink-0 items-center border-r border-gray-200 bg-gray-50 px-3 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-400">
                {prefix}
            </span>
            <input
                type={type}
                disabled={disabled}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="min-w-0 flex-1 border-0 bg-transparent px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-0 disabled:bg-gray-50 disabled:text-gray-500 dark:text-gray-200 dark:disabled:bg-gray-700/40 dark:disabled:text-gray-500"
            />
        </div>
    );
}

type AvatarUploadProps = {
    preview: string | null;
    onChange: (file: File | null) => void;
};

const FALLBACK = '/img/favicon.png';

export function AvatarUploadRow({ preview, onChange }: AvatarUploadProps) {
    return (
        <SettingsFormRow label="Foto de perfil" hint="Atualize sua foto. PNG, JPG ou WebP até 5MB.">
            <div className="flex flex-wrap items-start gap-4">
                <div className="size-20 shrink-0 overflow-hidden rounded-full border-2 border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-700">
                    <img
                        src={preview || FALLBACK}
                        alt="Avatar"
                        className="size-full object-cover"
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = FALLBACK;
                        }}
                    />
                </div>
                <FileDropzone
                    accept="image/png,image/jpeg,image/webp,image/*"
                    maxSizeMb={5}
                    label="Arraste a foto"
                    hint="PNG/JPG/WebP · clique para selecionar"
                    showSelected={false}
                    className="min-w-[14rem] flex-1 [&>label]:py-4"
                    onFiles={(files) => onChange(files[0] ?? null)}
                    onError={(msg) => toast.error(msg)}
                />
            </div>
        </SettingsFormRow>
    );
}
